<template>
  <v-card
    :outlined="!flat"
    :flat="flat"
    :loading="loading"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-if="$slots.image">
      <slot name="image" />
    </template>

    <template v-else-if="imageSrc">
      <v-img :src="imageSrc" :height="imageHeight">
        <v-container v-if="imageText">
          <v-row no-gutters>
            <v-col>
              <span class="text-h5 white--text">{{ imageText }}</span>
            </v-col>
          </v-row>
        </v-container>
      </v-img>
    </template>

    <v-toolbar v-if="!hideToolbar" flat :dense="dense">
      <!-- close button -->
      <v-btn v-if="dialog" icon @click="$emit('close')">
        <v-icon small> $freshvuecards-close </v-icon>
      </v-btn>

      <!-- Title -->
      <v-toolbar-title v-if="title || subtitle">
        <v-list-item :two-line="!!subtitle">
          <v-list-item-avatar v-if="$slots.titleAvatar">
            <slot name="titleAvatar" />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title
              v-if="title"
              :class="{ 'text-h5': !dense, 'text-h6': dense }"
            >
              {{ title }}
            </v-list-item-title>
            <template v-if="subtitle">
              <template v-if="subtitleVhtml">
                <!-- eslint-disable -->
                <v-list-item-subtitle
                  v-if="subtitle"
                  v-html="subtitle"
                ></v-list-item-subtitle>
                <!-- eslint-enable -->
              </template>
              <template v-else>
                <v-list-item-subtitle v-if="subtitle">
                  {{ subtitle }}
                </v-list-item-subtitle>
              </template>
            </template>
          </v-list-item-content>
        </v-list-item>
      </v-toolbar-title>

      <!-- Toolbar Actions (only on desktop) -->
      <template v-if="!$vuetify.breakpoint.mobile">
        <v-spacer />

        <!-- Toolbar Text Actions -->
        <v-toolbar-items v-if="$slots.toolbarTextActions">
          <slot name="toolbarTextActions" />
        </v-toolbar-items>

        <!-- Toolbar Icon Actions -->
        <slot name="toolbarIconActions" />

        <!-- close button -->
        <v-icon v-if="dialog && draggable" class="drag-handle">
          $freshvuecards-drag
        </v-icon>
      </template>

      <!-- Toolbar Extension Slot -->
      <template v-if="$slots.toolbarExtension" #extension>
        <slot name="toolbarExtension" />
      </template>
    </v-toolbar>

    <v-divider
      v-if="$slots.expandPrepend || (!hideContentDefaultSlot && !hideToolbar)"
    />

    <!-- Toolbar Actions (only on mobile) -->
    <div
      v-if="
        $vuetify.breakpoint.mobile &&
        !hideToolbar &&
        ($slots.toolbarTextActions || $slots.toolbarIconActions)
      "
    >
      <v-card-text class="d-flex justify-center">
        <!-- Toolbar Text Actions -->
        <v-toolbar-items v-if="$slots.toolbarTextActions">
          <slot name="toolbarTextActions" />
        </v-toolbar-items>

        <!-- Toolbar Icon Actions -->
        <slot name="toolbarIconActions" />
      </v-card-text>
      <v-divider />
    </div>

    <v-expand-transition>
      <div v-if="$slots.expandPrepend">
        <v-card-text>
          <slot name="expandPrepend" />
        </v-card-text>
        <v-divider />
      </div>
    </v-expand-transition>

    <!-- only show if there is a default slot (and it should be shown) or the content should be hidden (which means there is an error or success message) -->
    <v-card-text
      v-if="!hideContentDefaultSlot"
      :class="{
        'px-0': noPadding,
        'pt-0': noPadding && !$slots.expandPrepend,
        'pb-0':
          noPadding &&
          !(
            !$slots.expandAppend &&
            !hideActions &&
            (showActions || $slots.actions)
          ),
        'pa-3': dense && !noPadding
      }"
    >
      <template v-if="error">
        <slot v-if="$slots.error" name="error" />
        <v-alert v-else type="error" text border="left">
          {{ errorMessage }}
        </v-alert>
      </template>
      <template v-if="success">
        <slot v-if="$slots.success" name="success" />
        <v-alert v-else type="success" text border="left">
          {{ successMessage }}
        </v-alert>
      </template>

      <v-expand-transition>
        <div v-if="!hideContent">
          <!-- We will show a loading animation if the loadingBlockEnabled is set -->
          <template v-if="loadingBlockEnabled">
            <v-container v-if="loadingBlock">
              <v-row align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  class="ma-12"
                />
              </v-row>
            </v-container>
            <template v-else-if="!loadingBlockItemNotFound">
              <slot />
            </template>
            <template v-else>
              {{ loadingBlockItemNotFoundText }}
            </template>
          </template>
          <template v-else>
            <slot />
          </template>
        </div>
      </v-expand-transition>
    </v-card-text>

    <v-expand-transition>
      <div v-if="$slots.expandAppend">
        <v-divider />
        <v-card-text>
          <slot name="expandAppend" />
        </v-card-text>
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <template v-if="!hideActions && (showActions || $slots.actions)">
        <div v-if="$slots.actions">
          <v-divider />
          <v-card-actions>
            <slot name="actions" />
          </v-card-actions>
        </div>
        <div v-else>
          <v-divider />
          <v-card-actions>
            <v-btn
              v-if="showActionsDelete"
              color="error"
              :disabled="loading"
              text
              @click="$emit('delete')"
            >
              {{ buttonDeleteText }}
            </v-btn>
            <v-spacer />
            <slot name="RequestAccess" />
            <v-fade-transition v-if="linkTo && linkText">
              <router-link v-if="!hideLink" :to="linkTo" class="pr-4">
                {{ linkText }}
              </router-link>
            </v-fade-transition>
            <v-btn
              v-if="showActionsCancel"
              color="primary"
              :disabled="loading"
              text
              @click="$emit('cancel')"
            >
              {{ buttonCancelText }}
            </v-btn>
            <v-btn color="primary" :disabled="loading" @click="$emit('submit')">
              {{ buttonSubmitText }}
            </v-btn>
          </v-card-actions>
        </div>
      </template>
    </v-expand-transition>
  </v-card>
</template>

<script>
import {
  VCard,
  VCardText,
  VCardActions,
  VImg,
  VContainer,
  VRow,
  VCol,
  VToolbar,
  VToolbarTitle,
  VToolbarItems,
  VBtn,
  VIcon,
  VListItem,
  VListItemAvatar,
  VListItemContent,
  VListItemTitle,
  VListItemSubtitle,
  VSpacer,
  VDivider,
  VAlert,
  VExpandTransition,
  VFadeTransition,
  VProgressCircular
} from 'vuetify/lib'

export default {
  name: 'CardBase',

  components: {
    VCard,
    VCardText,
    VCardActions,
    VImg,
    VContainer,
    VRow,
    VCol,
    VToolbar,
    VToolbarTitle,
    VToolbarItems,
    VBtn,
    VIcon,
    VListItem,
    VListItemAvatar,
    VListItemContent,
    VListItemTitle,
    VListItemSubtitle,
    VSpacer,
    VDivider,
    VAlert,
    VExpandTransition,
    VFadeTransition,
    VProgressCircular
  },

  props: {
    flat: {
      type: Boolean,
      required: false,
      default: false
    },
    dense: {
      type: Boolean,
      required: false,
      default: false
    },
    noPadding: {
      type: Boolean,
      required: false,
      default: false
    },

    title: {
      type: String,
      required: false,
      default: null
    },
    subtitle: {
      type: String,
      required: false,
      default: null
    },
    subtitleVhtml: {
      type: Boolean,
      required: false,
      default: false
    },

    imageSrc: {
      type: String,
      required: false,
      default: null
    },
    imageText: {
      type: String,
      required: false,
      default: null
    },
    imageHeight: {
      type: String,
      required: false,
      default: '200px'
    },

    loading: {
      type: Boolean,
      default: false
    },
    loadingBlock: {
      type: Boolean,
      default: false
    },
    loadingBlockEnabled: {
      type: Boolean,
      default: false
    },
    loadingBlockItemNotFound: {
      type: Boolean,
      default: null
    },
    loadingBlockItemNotFoundText: {
      type: String,
      default: function () {
        return this.$t('fresh-vue-cards.misc.no-data')
      }
    },

    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      required: false,
      default: function () {
        return this.$t('fresh-vue-cards.misc.error')
      }
    },
    hideContentOnError: {
      type: Boolean,
      required: false,
      default: false
    },
    hideActionsOnError: {
      type: Boolean,
      required: false,
      default: false
    },

    success: {
      type: Boolean,
      default: false
    },
    successMessage: {
      type: String,
      required: false,
      default: function () {
        return this.$t('fresh-vue-cards.misc.success')
      }
    },
    hideContentOnSuccess: {
      type: Boolean,
      required: false,
      default: false
    },
    hideActionsOnSuccess: {
      type: Boolean,
      required: false,
      default: false
    },

    hideContentAlways: {
      type: Boolean,
      required: false,
      default: false
    },

    showActions: {
      type: Boolean,
      required: false,
      default: false
    },
    showActionsCancel: {
      type: Boolean,
      required: false,
      default: false
    },
    showActionsDelete: {
      type: Boolean,
      required: false,
      default: false
    },

    buttonSubmitText: {
      type: String,
      required: false,
      default: function () {
        return this.$t('fresh-vue-cards.misc.submit')
      }
    },
    buttonCancelText: {
      type: String,
      required: false,
      default: function () {
        return this.$t('fresh-vue-cards.misc.cancel')
      }
    },
    buttonDeleteText: {
      type: String,
      required: false,
      default: function () {
        return this.$t('fresh-vue-cards.misc.delete')
      }
    },

    hideLink: {
      type: Boolean,
      required: false,
      default: false
    },
    linkTo: {
      type: Object,
      required: false,
      default: null
    },
    linkText: {
      type: String,
      required: false,
      default: null
    },

    dialog: {
      type: Boolean,
      required: false,
      default: false
    },
    draggable: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  computed: {
    hideContent() {
      return (
        (this.error && this.hideContentOnError) ||
        (this.success && this.hideContentOnSuccess)
      )
    },

    hideContentDefaultSlot() {
      if (this.hideContentAlways) {
        return true
      }

      return !this.$slots.default && !this.hideContent
    },

    hideActions() {
      return (
        (this.error && this.hideActionsOnError) ||
        (this.success && this.hideActionsOnSuccess)
      )
    },

    hideToolbar() {
      return (
        !this.title &&
        !this.subtitle &&
        !this.$slots.toolbarTextActions &&
        !this.$slots.toolbarIconActions &&
        !this.$slots.toolbarExtension
      )
    }
  },

  watch: {
    error: {
      handler: function (val) {
        this.$nextTick(() => {
          if (!val) {
            this.$emit('show:content')
          } else {
            this.$emit('hide:content')
          }
        })
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.drag-handle {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}
</style>
